import React from "react";
import { Helmet } from "react-helmet-async";

export default function Vision() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-20">
      {/* ============================
          SEO
      ============================ */}
      <Helmet>
        <title>Vision | Aerzen Air™ Clean Indoor Air Mission</title>
        <meta
          name="description"
          content="Aerzen Air™ envisions a future where clean indoor air is a public health standard across Indian homes, workplaces, and shared environments."
        />
        <link
          rel="canonical"
          href="https://www.aerzenair.com/vision"
        />
      </Helmet>

      {/* ======================================================
          TITLE
      ====================================================== */}
      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Vision
        </h1>

        <p className="text-gray-700 max-w-3xl">
          If water can be purified as a basic necessity, air deserves the same
          level of attention.
        </p>
      </div>

      {/* ======================================================
          MAIN CONTENT GRID
      ====================================================== */}
      <div className="grid md:grid-cols-2 gap-12 items-start">

        {/* LEFT — CONTENT */}
        <div>
          <p className="text-gray-700 mb-6">
            <strong>AERZEN AIR™</strong> envisions a future where access to clean
            indoor air is normalized across homes, workplaces, and shared indoor
            environments — not as a luxury, but as a public health standard.
          </p>

          <p className="text-gray-700 mb-6">
            While purified drinking water has achieved widespread adoption in
            India, indoor air purification remains significantly under-penetrated,
            despite rising pollution levels and extended indoor exposure.
          </p>

          {/* INDICATIVE DATA */}
          <div className="bg-gray-50 border rounded-lg p-6 mb-6">
            <h3 className="font-semibold mb-4">
              Indicative Indoor Environment Context
            </h3>

            <ul className="text-gray-700 space-y-3 text-sm">
              <li>
                • Purified drinking water penetration: <strong>Widely adopted</strong> across urban households
              </li>
              <li>
                • Indoor air purification adoption: <strong>Limited and emerging</strong>
              </li>
              <li>
                • Estimated urban indoor exposure reach: <strong>~4.2 crore households</strong>
              </li>
            </ul>
          </div>

          {/* HEALTH CONTEXT */}
          <div>
            <h3 className="font-semibold mb-3">
              Indoor Air & Health Awareness
            </h3>

            <p className="text-gray-700 text-sm mb-4">
              Prolonged exposure to polluted indoor air is associated with increased risk of
              respiratory discomfort and conditions such as asthma, allergic sensitivities,
              chronic respiratory irritation, and reduced overall lung function. Children,
              the elderly, and individuals with pre-existing respiratory conditions are
              particularly vulnerable due to longer indoor exposure durations.
            </p>

            <p className="text-gray-700 text-sm mb-4">
              Modern indoor lifestyles — including extended time spent in enclosed homes,
              offices, classrooms, and transit environments — often result in limited natural
              ventilation. This allows fine particulate matter (PM2.5 and PM10), allergens,
              and indoor-generated pollutants to accumulate beyond safe thresholds.
            </p>

            <p className="text-gray-700 text-sm">
              Increasing awareness of indoor air quality is therefore a critical public
              health step. Measurement, visibility, and informed decision-making form the
              foundation for meaningful improvement — enabling households and institutions
              to move from assumption-based comfort to data-backed environmental safety.
            </p>
          </div>
        </div>

        {/* RIGHT — IMAGE */}
        <div>
          <img
            src="/images/vision/vision-indoor-air.jpg"
            alt="Indoor air quality awareness and monitoring"
            className="w-full rounded-xl border"
          />
        </div>
      </div>

      {/* ======================================================
          CLOSING STATEMENT
      ====================================================== */}
      <div className="mt-12 max-w-4xl">
        <p className="text-gray-700">
          The vision emphasizes clarity, education, and measured adoption —
          focusing on practical, data-informed air purification suited to Indian
          indoor conditions rather than short-term claims or exaggerated narratives.
        </p>
      </div>
    </section>
  );
}
